import {
  SiQuickbooks,
  SiQuickbooksHex,
  SiXero,
  SiXeroHex,
} from "@icons-pack/react-simple-icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import { ArrowRight, Ruler, Sheet } from "lucide-react";
import { accountingCommonNames } from "shared";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { restrictRoute } from "@/lib/routeBlocker";

export const Route = createFileRoute("/_authed/_org/accounting/settings")({
  beforeLoad: async ({ context }) =>
    restrictRoute(context, {
      redirect: {
        to: "/accounting",
      },
      requiredRole: "bookkeeper",
    }),
  component: RouteComponent,
});

function RouteComponent() {
  const location = useLocation();
  const { trpc } = Route.useRouteContext();

  const { data: accounting } = useSuspenseQuery(
    trpc.accounting.get.queryOptions(),
  );
  const type = accounting?.type ?? "quickbooks";

  if (location.pathname === "/accounting/settings") {
    return (
      <div className="flex h-full flex-col">
        <SheetHeader className="border-b p-6">
          <SheetTitle className="flex flex-row items-center">
            {type === "quickbooks" && (
              <SiQuickbooks color={SiQuickbooksHex} className="size-8" />
            )}
            {type === "quickbooks-desktop" && (
              <SiQuickbooks color="#25661d" className="size-8" />
            )}
            {type === "xero" && <SiXero color={SiXeroHex} className="size-8" />}

            <span className="px-2">{accountingCommonNames[type]} Settings</span>
          </SheetTitle>

          <SheetDescription>
            Manage your {accountingCommonNames[type]} integration by setting
            custom coding rules and configuring your chart of accounts
          </SheetDescription>
        </SheetHeader>
        <div className="flex w-full flex-col items-center space-y-4 px-4">
          <div className="mt-4 flex w-full flex-col space-y-4">
            <Link to="/accounting/settings/fields">
              <Card className="cursor-pointer transition-all duration-200 hover:border-primary/20 hover:bg-muted/30">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sheet className="size-5 text-neutral-500" />
                      Accounting Fields
                    </div>
                    <ArrowRight className="size-4 text-muted-foreground" />
                  </CardTitle>
                  <CardDescription>
                    Manage, enable, and disable custom accounting fields
                  </CardDescription>
                </CardHeader>
              </Card>
            </Link>

            <Link to="/accounting/settings/rules">
              <Card className="cursor-pointer transition-all duration-200 hover:border-primary/20 hover:bg-muted/30">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Ruler className="size-5 text-neutral-500" />
                      Coding Rules
                    </div>
                    <ArrowRight className="size-4 text-muted-foreground" />
                  </CardTitle>

                  <CardDescription>
                    Assign expense accounts automatically
                  </CardDescription>
                </CardHeader>
              </Card>
            </Link>
          </div>
        </div>
      </div>
    );
  }
  return <Outlet />;
}
